const TOAST_EVENT_NAME = 'datatru:toast';

const TONE_CLASSES = {
  success:
    'border-green-200 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200',
  info: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200',
  warning:
    'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200',
  error:
    'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200',
};

const DEFAULT_DURATION_MS = {
  success: 3400,
  info: 4200,
};

const DEFAULT_STICKY = {
  success: false,
  info: false,
  warning: true,
  error: true,
};

const DEFAULT_OPTIONS = {
  maxVisible: 3,
  maxQueue: 24,
  dedupeWindowMs: 1200,
  position: 'top-center',
  defaultDurationMs: null,
};

const STACK_BASE_CLASSES =
  'pointer-events-none fixed inset-x-4 z-[120] flex flex-col gap-2 sm:inset-x-auto';

const STACK_POSITION_CLASSES = {
  'top-left': 'top-20 items-start sm:left-6',
  'top-center': 'top-20 items-center sm:left-1/2 sm:-translate-x-1/2',
  'top-right': 'top-20 items-end sm:right-6',
  'bottom-left': 'bottom-6 items-start sm:left-6',
  'bottom-center': 'bottom-6 items-center sm:left-1/2 sm:-translate-x-1/2',
  'bottom-right': 'bottom-6 items-end sm:right-6',
};

let toastManagerInstance = null;
let listenerBound = false;

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const toPositiveIntOrNull = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return Math.floor(parsed);
};

const normalizeTone = (value) => {
  const tone = String(value || '')
    .trim()
    .toLowerCase();
  if (tone === 'success' || tone === 'warning' || tone === 'error') {
    return tone;
  }
  return 'info';
};

const normalizeAction = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if (payload.action && typeof payload.action === 'object') {
    const label = String(payload.action.label || '').trim();
    const href = String(payload.action.href || '').trim();
    if (label && href) {
      return { label, href };
    }
  }

  const actionLabel = String(
    payload.actionLabel || payload.linkLabel || '',
  ).trim();
  const actionHref = String(
    payload.actionHref || payload.linkHref || '',
  ).trim();
  if (!actionLabel || !actionHref) {
    return null;
  }
  return { label: actionLabel, href: actionHref };
};

const normalizeViewportPosition = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
  if (hasOwn(STACK_POSITION_CLASSES, normalized)) {
    return normalized;
  }
  return DEFAULT_OPTIONS.position;
};

const normalizeToastPayload = (payload, options = {}) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const message = String(payload.message || '').trim();
  if (!message) {
    return null;
  }

  const tone = normalizeTone(payload.tone);
  const action = normalizeAction(payload);
  const durationMs = toPositiveIntOrNull(
    payload.durationMs ||
      payload.duration_ms ||
      payload.autoDismissMs ||
      payload.auto_dismiss_ms,
  );
  const hasExplicitDuration = durationMs !== null;
  const stickyOverride =
    typeof payload.sticky === 'boolean' ? payload.sticky : null;
  const sticky =
    stickyOverride !== null
      ? stickyOverride
      : hasExplicitDuration
        ? false
        : action
          ? true
          : DEFAULT_STICKY[tone];
  const optionDefaultDurationMs = toPositiveIntOrNull(
    options.defaultDurationMs,
  );
  const resolvedDurationMs = sticky
    ? null
    : (durationMs ??
      optionDefaultDurationMs ??
      DEFAULT_DURATION_MS[tone] ??
      4200);

  const dedupeKey = String(
    payload.dedupeKey || payload.dedupe_key || `${tone}:${message}`,
  ).trim();

  const urgent =
    typeof payload.urgent === 'boolean'
      ? payload.urgent
      : tone === 'warning' || tone === 'error';

  return {
    id: '',
    message,
    tone,
    sticky,
    durationMs: resolvedDurationMs,
    action,
    dedupeKey: dedupeKey || `${tone}:${message}`,
    urgent,
    scope: String(payload.scope || '').trim(),
  };
};

class ToastManager {
  constructor(root, options = {}) {
    this.root = root;
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options,
    };
    this.visible = [];
    this.queue = [];
    this.dedupeMap = new Map();
    this.timerState = new Map();
    this.nextId = 0;

    this.stack = document.createElement('div');
    this._applyStackPosition(this.options.position);
    this.stack.setAttribute('role', 'region');
    this.stack.setAttribute('aria-label', 'Notifications');
    this.root.appendChild(this.stack);

    this._boundVisibilityHandler = this._handleVisibilityChange.bind(this);
    document.addEventListener(
      'visibilitychange',
      this._boundVisibilityHandler,
      false,
    );
  }

  configure(nextOptions = {}) {
    if (!nextOptions || typeof nextOptions !== 'object') {
      return;
    }

    if (
      hasOwn(nextOptions, 'position') ||
      hasOwn(nextOptions, 'toastPosition') ||
      hasOwn(nextOptions, 'toast_position')
    ) {
      this._applyStackPosition(
        nextOptions.position ||
          nextOptions.toastPosition ||
          nextOptions.toast_position,
      );
    }

    if (
      hasOwn(nextOptions, 'defaultDurationMs') ||
      hasOwn(nextOptions, 'default_duration_ms')
    ) {
      this.options.defaultDurationMs = toPositiveIntOrNull(
        nextOptions.defaultDurationMs || nextOptions.default_duration_ms,
      );
    }
  }

  destroy() {
    document.removeEventListener(
      'visibilitychange',
      this._boundVisibilityHandler,
      false,
    );
    this.timerState.forEach((state) => {
      if (state.timeoutId) {
        window.clearTimeout(state.timeoutId);
      }
    });
    this.timerState.clear();
    this.visible = [];
    this.queue = [];
    this.dedupeMap.clear();
    if (this.stack && this.stack.parentNode) {
      this.stack.parentNode.removeChild(this.stack);
    }
  }

  enqueue(inputPayload) {
    const toast = normalizeToastPayload(inputPayload, this.options);
    if (!toast) {
      return null;
    }

    const now = Date.now();
    this._purgeExpiredDedupeEntries(now);
    const lastSeen = this.dedupeMap.get(toast.dedupeKey) || 0;
    if (now - lastSeen <= this.options.dedupeWindowMs) {
      return null;
    }
    this.dedupeMap.set(toast.dedupeKey, now);

    toast.id = `toast-${now}-${this.nextId + 1}`;
    this.nextId += 1;

    if (this.visible.length < this.options.maxVisible) {
      this._showToast(toast);
      return toast.id;
    }

    this.queue.push(toast);
    if (this.queue.length > this.options.maxQueue) {
      this.queue.shift();
    }
    return toast.id;
  }

  dismiss(id) {
    const visibleToast = this.visible.find((item) => item.id === id);
    if (!visibleToast) {
      this.queue = this.queue.filter((item) => item.id !== id);
      return;
    }

    this._clearTimer(visibleToast.id);
    this.visible = this.visible.filter((item) => item.id !== id);
    visibleToast.element.classList.add('translate-y-1', 'opacity-0');
    window.setTimeout(() => {
      visibleToast.element.remove();
      this._promoteQueuedToasts();
    }, 170);
  }

  seedFromDocument() {
    const nodes = document.querySelectorAll(
      '[data-toast-seed][data-toast-payload]',
    );
    nodes.forEach((node) => {
      const payload = node.getAttribute('data-toast-payload');
      if (!payload) {
        node.remove();
        return;
      }
      try {
        this.enqueue(JSON.parse(payload));
      } catch (error) {
        // Ignore malformed payloads and continue.
      }
      node.remove();
    });
  }

  _showToast(toast) {
    const element = this._renderToast(toast);
    this.stack.appendChild(element);
    this.visible.push({ id: toast.id, element, toast });

    window.requestAnimationFrame(() => {
      element.classList.remove('opacity-0', 'translate-y-1');
    });

    if (!toast.sticky && toast.durationMs) {
      this._createTimerState(toast.id, toast.durationMs, element);
      this._resumeTimer(toast.id);
    }
  }

  _renderToast(toast) {
    const element = document.createElement('div');
    const toneClasses = TONE_CLASSES[toast.tone] || TONE_CLASSES.info;
    element.className = `pointer-events-auto relative flex max-w-sm items-start gap-3 rounded-md border px-3 py-2 text-sm shadow-lg transition duration-150 ease-out opacity-0 translate-y-1 ${toneClasses}`;
    element.dataset.toastId = toast.id;
    element.setAttribute('tabindex', '0');
    element.setAttribute('role', toast.urgent ? 'alert' : 'status');
    element.setAttribute('aria-live', toast.urgent ? 'assertive' : 'polite');

    const message = document.createElement('p');
    message.className = 'min-w-0 flex-1 pr-2';
    message.textContent = toast.message;
    element.appendChild(message);

    if (toast.action) {
      const action = document.createElement('a');
      action.className =
        'inline-flex shrink-0 items-center font-semibold text-current underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-current/40';
      action.href = toast.action.href;
      action.textContent = toast.action.label;
      element.appendChild(action);
    }

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className =
      'inline-flex shrink-0 items-center justify-center rounded-md size-5 text-current/70 transition hover:bg-black/5 hover:text-current focus:outline-none focus:ring-2 focus:ring-current/40 dark:hover:bg-white/10';
    closeButton.setAttribute('aria-label', 'Dismiss notification');
    closeButton.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" class="size-3.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M5 5l10 10M15 5 5 15"/></svg>';
    closeButton.addEventListener('click', () => this.dismiss(toast.id));
    element.appendChild(closeButton);

    element.addEventListener('mouseenter', () => this._pauseTimer(toast.id));
    element.addEventListener('focusin', () => this._pauseTimer(toast.id));
    element.addEventListener('mouseleave', () =>
      this._resumeIfActiveAndInteractiveStateAllows(toast.id),
    );
    element.addEventListener('focusout', () => {
      window.setTimeout(() => {
        this._resumeIfActiveAndInteractiveStateAllows(toast.id);
      }, 0);
    });

    return element;
  }

  _promoteQueuedToasts() {
    while (this.visible.length < this.options.maxVisible && this.queue.length) {
      const nextToast = this.queue.shift();
      if (nextToast) {
        this._showToast(nextToast);
      }
    }
  }

  _purgeExpiredDedupeEntries(now) {
    const cutoff = now - this.options.dedupeWindowMs;
    this.dedupeMap.forEach((timestamp, key) => {
      if (timestamp < cutoff) {
        this.dedupeMap.delete(key);
      }
    });
  }

  _createTimerState(id, durationMs, element) {
    this.timerState.set(id, {
      timeoutId: null,
      startedAt: 0,
      remainingMs: durationMs,
      element,
    });
  }

  _clearTimer(id) {
    const state = this.timerState.get(id);
    if (!state) {
      return;
    }
    if (state.timeoutId) {
      window.clearTimeout(state.timeoutId);
    }
    this.timerState.delete(id);
  }

  _pauseTimer(id) {
    const state = this.timerState.get(id);
    if (!state || !state.timeoutId) {
      return;
    }
    window.clearTimeout(state.timeoutId);
    state.timeoutId = null;
    state.remainingMs = Math.max(
      0,
      state.remainingMs - (Date.now() - state.startedAt),
    );
  }

  _resumeTimer(id) {
    const state = this.timerState.get(id);
    if (!state || state.timeoutId || state.remainingMs <= 0) {
      return;
    }
    state.startedAt = Date.now();
    state.timeoutId = window.setTimeout(() => {
      this.dismiss(id);
    }, state.remainingMs);
  }

  _resumeIfActiveAndInteractiveStateAllows(id) {
    const state = this.timerState.get(id);
    if (!state || document.hidden) {
      return;
    }
    if (!state.element || !state.element.isConnected) {
      return;
    }
    const activeElement = document.activeElement;
    const containsFocus =
      activeElement instanceof Node && state.element.contains(activeElement);
    const hovered = state.element.matches(':hover');
    if (containsFocus || hovered) {
      return;
    }
    this._resumeTimer(id);
  }

  _handleVisibilityChange() {
    if (document.hidden) {
      this.timerState.forEach((_, id) => this._pauseTimer(id));
      return;
    }
    this.timerState.forEach((_, id) =>
      this._resumeIfActiveAndInteractiveStateAllows(id),
    );
  }

  _applyStackPosition(position) {
    const normalizedPosition = normalizeViewportPosition(position);
    this.options.position = normalizedPosition;
    this.stack.className = `${STACK_BASE_CLASSES} ${STACK_POSITION_CLASSES[normalizedPosition]}`;
  }
}

const ensureRootElement = () => {
  let root = document.querySelector('[data-toast-viewport-root]');
  if (root) {
    return root;
  }
  root = document.createElement('div');
  root.setAttribute('data-toast-viewport-root', '');
  document.body.appendChild(root);
  return root;
};

const getRootOptions = (root) => {
  if (!root || !(root instanceof HTMLElement)) {
    return {};
  }

  const options = {};
  const position = root.dataset.toastPosition || '';
  const defaultDurationMs = root.dataset.toastDefaultDurationMs || '';

  if (position) {
    options.position = position;
  }
  if (defaultDurationMs) {
    options.defaultDurationMs = defaultDurationMs;
  }
  return options;
};

const initializeToasts = (options = {}) => {
  if (toastManagerInstance) {
    if (options && typeof options === 'object' && Object.keys(options).length) {
      toastManagerInstance.configure(options);
    }
    toastManagerInstance.seedFromDocument();
    return toastManagerInstance;
  }

  const root = ensureRootElement();
  toastManagerInstance = new ToastManager(root, {
    ...getRootOptions(root),
    ...(options && typeof options === 'object' ? options : {}),
  });
  toastManagerInstance.seedFromDocument();

  if (!listenerBound) {
    window.addEventListener(TOAST_EVENT_NAME, (event) => {
      const detail = event && event.detail ? event.detail : null;
      toastManagerInstance.enqueue(detail);
    });
    listenerBound = true;
  }

  window.DataTru = window.DataTru || {};
  window.DataTru.toasts = {
    enqueue: (payload) => toastManagerInstance.enqueue(payload),
    configure: (nextOptions) => toastManagerInstance.configure(nextOptions),
    eventName: TOAST_EVENT_NAME,
  };

  return toastManagerInstance;
};

const enqueueToast = (payload) => {
  const manager = initializeToasts();
  return manager.enqueue(payload);
};

const emitToast = (payload) => {
  window.dispatchEvent(
    new CustomEvent(TOAST_EVENT_NAME, {
      detail: payload,
    }),
  );
};

export { TOAST_EVENT_NAME, enqueueToast, emitToast, initializeToasts };
